import React, { useEffect, useState } from 'react';
import { Button, Empty, Input, Modal, Popconfirm, Skeleton, Tooltip, Typography, message } from 'antd';
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import todoCategoryService from '../../../services/todoCategoryService';
import TodoLabelTag, { LABEL_TONES, solidLabelStyle } from './TodoLabelTag';

const { Text } = Typography;

/**
 * A row of colour choices.
 *
 * Swatches rather than a dropdown: colour is the one property you pick by looking, and
 * a select would hide every option behind a click and describe them in words. The
 * current choice carries a tick, so it survives being seen by someone who cannot
 * distinguish two of the tones.
 */
const ColorSwatches = ({ value, onChange, disabled }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {LABEL_TONES.map((tone) => {
            const selected = (value || 'default') === tone.value;

            return (
                <Tooltip key={tone.value} title={tone.label}>
                    <button
                        type="button"
                        aria-label={tone.label}
                        aria-pressed={selected}
                        disabled={disabled}
                        onClick={() => onChange(tone.value)}
                        style={{
                            width: 24,
                            height: 24,
                            padding: 0,
                            borderRadius: 6,
                            cursor: disabled ? 'default' : 'pointer',
                            border: selected
                                ? '2px solid var(--ant-color-text)'
                                : '1px solid var(--ant-color-border)',
                            background: 'transparent',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            // The tick is absolutely positioned; without this it would
                            // anchor to the page instead of the swatch.
                            position: 'relative',
                        }}
                    >
                        {/* The swatch is painted with the SAME fill the label will use,
                            so what you pick is literally what you get. */}
                        <span
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: solidLabelStyle(tone.value).background,
                            }}
                        />
                        {selected && (
                            <CheckOutlined
                                style={{
                                    position: 'relative',
                                    fontSize: 11,
                                    // Contrasts against the fill it sits on, not the page.
                                    color: solidLabelStyle(tone.value).color,
                                }}
                            />
                        )}
                    </button>
                </Tooltip>
            );
        })}
    </div>
);

/**
 * Label management.
 *
 * One modal, edited in place — the previous version was a table inside a modal that
 * opened a SECOND modal to add or edit a row, so creating two labels meant four dialog
 * transitions. Labels are a name and a colour; that does not warrant a form.
 *
 * Matches the board column manager deliberately: same inline editing, same save-on-blur,
 * same add-row at the bottom. They are the two "small taxonomy" screens in this feature
 * and behaving differently would be the only thing to learn about either.
 */
const TodoCategoryManage = ({ visible, onClose, categories: initial = [], onRefresh }) => {
    const [labels, setLabels] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState('blue');

    const load = async () => {
        setLoading(true);
        try {
            const response = await todoCategoryService.getCategories();
            const result = response?.data?.data ?? response?.data ?? [];
            setLabels(Array.isArray(result) ? result : []);
        } catch (e) {
            message.error('Could not load labels.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const run = async (fn, successMessage) => {
        setBusy(true);
        try {
            await fn();
            await load();
            onRefresh?.();
            if (successMessage) message.success(successMessage);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Could not save that change.');
        } finally {
            setBusy(false);
        }
    };

    const add = () => {
        const name = newName.trim();
        if (!name) return;

        run(async () => {
            await todoCategoryService.createCategory({ name, color: newColor });
            setNewName('');
        }, 'Label added');
    };

    return (
        <Modal
            title="Labels"
            open={visible}
            onCancel={onClose}
            footer={<Button onClick={onClose}>Done</Button>}
            width={560}
            destroyOnClose
        >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
                Labels group tasks across columns. A task can carry several.
            </Text>

            {loading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
            ) : labels.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No labels yet" />
            ) : (
                labels.map((label, index) => (
                    <div
                        key={label.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 0',
                            borderTop: index === 0 ? 'none' : '1px solid var(--ant-color-border-secondary)',
                        }}
                    >
                        {/* Shows the label exactly as it appears on a card. */}
                        <TodoLabelTag color={label.color} style={{ marginInlineEnd: 0, flex: 'none', minWidth: 64, textAlign: 'center' }}>
                            {label.name}
                        </TodoLabelTag>

                        <Input
                            size="small"
                            defaultValue={label.name}
                            disabled={busy}
                            style={{ flex: 1, minWidth: 120 }}
                            // Saved on blur, not per keystroke — one request per edit.
                            onBlur={(e) => {
                                const name = e.target.value.trim();
                                if (name && name !== label.name) {
                                    run(() => todoCategoryService.updateCategory(label.id, { name, color: label.color }));
                                }
                            }}
                            onPressEnter={(e) => e.target.blur()}
                        />

                        <ColorSwatches
                            value={label.color}
                            disabled={busy}
                            onChange={(color) => run(() => todoCategoryService.updateCategory(label.id, { name: label.name, color }))}
                        />

                        <Popconfirm
                            title="Delete this label?"
                            description="It will be removed from any task using it."
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => run(() => todoCategoryService.deleteCategory(label.id), 'Label deleted')}
                        >
                            <Button
                                size="small"
                                type="text"
                                danger
                                aria-label={`Delete ${label.name}`}
                                icon={<DeleteOutlined />}
                                disabled={busy}
                            />
                        </Popconfirm>
                    </div>
                ))
            )}

            <div
                style={{
                    marginTop: 20,
                    paddingTop: 16,
                    borderTop: '1px solid var(--ant-color-border-secondary)',
                }}
            >
                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Add a label</Text>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <Input
                        value={newName}
                        placeholder="Label name"
                        disabled={busy}
                        onChange={(e) => setNewName(e.target.value)}
                        onPressEnter={add}
                        style={{ flex: 1 }}
                    />
                    <Button type="primary" icon={<PlusOutlined />} loading={busy} disabled={!newName.trim()} onClick={add}>
                        Add
                    </Button>
                </div>

                <ColorSwatches value={newColor} onChange={setNewColor} disabled={busy} />
            </div>
        </Modal>
    );
};

export default TodoCategoryManage;
